import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Loader2 } from 'lucide-react';
import { salesAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { AnnualBarChart, MONTH_NAMES, isFutureMonth } from './AnnualBarChart';

interface MonthSummary {
  month: number;
  count: number;
  gross: number;
  net: number;
  fees: number;
  byType: Record<string, { count: number; net: number }>;
}

interface AnnualSummary {
  year: number;
  months: MonthSummary[];
  totals: Omit<MonthSummary, 'month'>;
}

const TYPES: { key: string; label: string }[] = [
  { key: 'NEW_CLIENT', label: 'Cliente novo' },
  { key: 'RENEWAL', label: 'Renovação' },
  { key: 'CONSULTORIA', label: 'Consultoria' },
  { key: 'GEP', label: 'GEP' },
];

function Stat({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
  return (
    <Card>
      <CardContent className="py-5">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-2 text-2xl font-bold">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

const money = (value: number) => (value ? formatCurrency(value) : '—');

/** Valor do tipo de venda com a quantidade logo abaixo */
function TypeCell({ value, padding = 'py-2.5' }: { value?: { count: number; net: number }; padding?: string }) {
  if (!value?.count) return <td className={`${padding} px-3 text-right tabular-nums`}>—</td>;
  return (
    <td className={`${padding} px-3 text-right tabular-nums`}>
      <div>{formatCurrency(value.net)}</div>
      <div className="text-xs font-normal text-muted-foreground">
        {value.count} {value.count === 1 ? 'venda' : 'vendas'}
      </div>
    </td>
  );
}

/**
 * Receitas (vendas aprovadas) do ano, mês a mês: líquido, bruto, taxas e por tipo de venda.
 */
export function SalesAnnualView({ year, onSelectMonth }: { year: number; onSelectMonth: (month: number) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['sales', 'annual', year],
    queryFn: async () => (await salesAPI.getAnnualSummary(year)).data.data as AnnualSummary,
  });

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { totals } = data;
  const monthsWithSales = data.months.filter((m) => m.count > 0).length;
  const best = data.months.reduce<MonthSummary | null>((top, m) => (!top || m.net > top.net ? m : top), null);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat title={`Receita líquida de ${year}`} value={formatCurrency(totals.net)} subtitle={`Bruto ${formatCurrency(totals.gross)} − taxas ${formatCurrency(totals.fees)}`} />
        <Stat title="Média mensal" value={formatCurrency(monthsWithSales ? totals.net / monthsWithSales : 0)} subtitle={`${monthsWithSales} ${monthsWithSales === 1 ? 'mês' : 'meses'} com vendas`} />
        <Stat title="Vendas aprovadas" value={totals.count.toLocaleString('pt-BR')} subtitle={`Ticket médio líquido ${formatCurrency(totals.count ? totals.net / totals.count : 0)}`} />
        <Stat title="Melhor mês" value={best && best.net ? formatCurrency(best.net) : '—'} subtitle={best && best.net ? MONTH_NAMES[best.month - 1] : 'Sem vendas'} />
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Receita líquida por mês</h2>
            <p className="text-sm text-muted-foreground">Vendas aprovadas do Mercado Pago. Clique em um mês para ver as vendas.</p>
          </div>
          <AnnualBarChart
            data={data.months.map((m) => ({ month: m.month, value: m.net, isForecast: isFutureMonth(year, m.month) }))}
            color="#3b82f6"
            onSelectMonth={onSelectMonth}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-3 px-3 text-left font-medium">Mês</th>
                  <th className="py-3 px-3 text-right font-medium">Vendas</th>
                  <th className="py-3 px-3 text-right font-medium">Bruto</th>
                  <th className="py-3 px-3 text-right font-medium">Taxas</th>
                  {TYPES.map((t) => (
                    <th key={t.key} className="py-3 px-3 text-right font-medium whitespace-nowrap">{t.label}</th>
                  ))}
                  <th className="py-3 px-3 text-right font-semibold text-foreground">Líquido</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => {
                  const future = isFutureMonth(year, m.month);
                  return (
                    <tr
                      key={m.month}
                      onClick={() => onSelectMonth(m.month)}
                      className={`border-b cursor-pointer hover:bg-accent/50 transition-colors ${future ? 'text-muted-foreground' : ''}`}
                    >
                      <td className="py-2.5 px-3 font-medium whitespace-nowrap">{MONTH_NAMES[m.month - 1]}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{m.count || '—'}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{money(m.gross)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{m.fees ? `− ${formatCurrency(m.fees)}` : '—'}</td>
                      {TYPES.map((t) => (
                        <TypeCell key={t.key} value={m.byType[t.key]} />
                      ))}
                      <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{money(m.net)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-3 px-3">Total {year}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{totals.count || '—'}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{money(totals.gross)}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{totals.fees ? `− ${formatCurrency(totals.fees)}` : '—'}</td>
                  {TYPES.map((t) => (
                    <TypeCell key={t.key} value={totals.byType[t.key]} padding="py-3" />
                  ))}
                  <td className="py-3 px-3 text-right tabular-nums">{money(totals.net)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
